import { TFunction, useTranslation } from "react-i18next";

import { CompanyDashboard, DashboardBase, PrivateDashboard } from "@/data/dashboard/types";
import { openJobApplicationStatuses } from "@/data/job-applications/constants";
import { openWorkOrderStatuses } from "@/data/work-orders";
import { router } from "@/views/router";

import { DashboardInfoStackProps } from "../components/DashboardInfoStack";

const getCompanyStats = (
  t: TFunction<"session", undefined>,
  data: CompanyDashboard,
  contactPersonId?: Number,
): DashboardInfoStackProps[] => [
  {
    number: data.openApplications,
    label: t("DASHBOARD.STATS_SECTION.OPEN_APPLICATIONS"),
    route: router.get(
      "job-applications",
      {},
      {
        filtersConfig: {
          ...(contactPersonId && { contactPersonProfileIds: [contactPersonId] }),
          status: openJobApplicationStatuses,
        },
      },
    ).path,
  },
  {
    number: data.ongoingAssignments,
    label: t("DASHBOARD.STATS_SECTION.ONGOING_ASSIGNMENTS"),
    route: router.get(
      "assignments",
      {},
      {
        filtersConfig: {
          ...(contactPersonId && { contactPersonId: [contactPersonId] }),
          status: openWorkOrderStatuses,
        },
      },
    ).path,
  },
  {
    number: data.myTeam,
    label: t("DASHBOARD.STATS_SECTION.MY_TEAM"),
    route: router.get(
      "companies.team",
      {},
      {
        filtersConfig: {
          ...(contactPersonId && { contactPersonProfileIds: [contactPersonId] }),
        },
      },
    ).path,
  },
];

const getPrivateStats = (t: TFunction<"session", undefined>, data: PrivateDashboard): DashboardInfoStackProps[] => [
  {
    number: data.myOpenApplications,
    label: t("DASHBOARD.STATS_SECTION.MY_OPEN_APPLICATIONS"),
    route: router.get("job-applications").path,
  },
  {
    number: data.savedJobs ?? 0,
    label: t("DASHBOARD.JOBS_SECTION.SAVED.TAB"),
    route: router.get("dashboard.saved").path,
  },
  {
    number: data.recommendedJobs ?? 0,
    label: t("DASHBOARD.JOBS_SECTION.RECOMMENDED.TAB"),
    route: router.get("dashboard.recommended").path,
  },
];

const extractCommonData = (data?: DashboardBase) => ({
  openJobs: data?.openJobs,
  recommendedJobs: data?.recommendedJobs,
  savedJobs: data?.savedJobs,
});

export const useDashboardFormats = () => {
  const { t } = useTranslation("session");

  const formatAdminDashboard = (data: CompanyDashboard) => ({
    ...extractCommonData(data),
    stats: getCompanyStats(t, data, undefined),
  });

  const formatManagerDashboard = (data: CompanyDashboard, contactPersonId?: Number) => ({
    ...extractCommonData(data),
    stats: getCompanyStats(t, data, contactPersonId),
  });

  const formatPrivateDashboard = (data: PrivateDashboard) => ({
    ...extractCommonData(data),
    stats: getPrivateStats(t, data),
  });

  return {
    formatAdminDashboard,
    formatManagerDashboard,
    formatPrivateDashboard,
  };
};
